import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import { logoutRedirect } from "src/async/fetch";
import MordernDesign from "src/components/container/MordernDesign";
import NavigationRouter from "src/components/navigation/NavigationRouter";
import Header from "src/components/shared/Header";
import Preloader from "src/components/shared/LoadingAnimations/Preloader";
import LogoutTimer from "src/components/shared/LogoutTimer";
import NotificationBanner from "src/components/shared/NotificationBanner";
import { outerPages } from "src/config/host";
import { AuthContext } from "src/context/auth";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import { useSocket } from "src/context/SocketContext";
import { WorkspaceContext } from "src/context/workspace";
import { searchMenu } from "src/helpers";
import { classNames } from "src/helpers/classNames";
import { getNavigateLink } from "src/helpers/userHelper";
import useActivePage from "src/hooks/useActivePage";
import useDevice from "src/hooks/useDevice";
import useSocketListeners from "src/hooks/useSocketListeners";
import useUrlQueryParams from "src/hooks/useUrlQueryParams";
import useWindowDimensions from "src/hooks/useWindowDimensions";

const Dashboardlayout = ({ nested = false }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [sidepanelWidth, setSidepanelWidth] = useState("336px");
  const [useCustomSidbar, setUseCustomSidbar] = useState(false);
  const [customSidebarHtml, setCustomSidebarHtml] = useState("");

  const device = useDevice();
  const socket = useSocket();

  const [isMenu, setIsMenu] = useState(true);
  const [menuType, setMenuType] = useState("vertical");
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceDetailsLoading, setWorkspaceDetailsLoading] = useState(true);
  const [workspaceDetails, setWorkspaceDetails] = useState({});
  const [workspacePaymentModalOpen, setWorspacePaymentModalOpen] = useState(false);
  const [planDetails, setPlanDetails] = useState({});
  const [isPlanDetailsLoaded, setIsPlanDetailsLoaded] = useState(false);

  const [keyword, setKeyword] = useState("");
  const [menus, setMenus] = useState([]);
  const [menusLoaded, setMenusLoaded] = useState(false);

  const [simulatedMenus, setSimulatedMenus] = useState([]);
  const [activeNotifications, setActiveNotifications] = useState({});
  const [mordernDesign, setMordernDesign] = useState(true);

  const navigate = useNavigate();

  const { user } = useContext(AuthContext);
  const { site } = useSiteSettings();

  const [params, setParams] = useState({ workspace_id: null, group_id: null, page_id: null });

  const { workspace_id } = useParams();
  const { group: group_id, page: page_id, document: document_id, form: form_id } = useUrlQueryParams();

  const elementRef = useRef(null);
  const [noticeHeight, setNoticeHeight] = useState(0);
  const { height } = useWindowDimensions();

  const headerHeight = device === "mobile" && site?.header_menu_height > 0 ? (`${site.header_menu_height}`.endsWith("px") || `${site.header_menu_height}`.endsWith("rem") ? site.header_menu_height : `${site.header_menu_height}px`) : "4rem";

  const menu_required = window.location.href.includes("/embed") || window.location.href.includes("/menu");
  const payment_required = window.location.href.includes("/payment-required");
  const no_content = window.location.href.includes("/permission-denied") || window.location.href.includes("/no-content");
  const workspace_paused = window.location.href.includes("/workspace-paused");

  const allowed_types = ["site_name", "square_logo", "navigation_layout", "color_logos"];

  const { type: page_type, data: activePageData } = useActivePage({
    menus,
    page_id,
    document_id,
    form_id,
  });

  useEffect(() => {
    setKeyword("");
  }, [workspace_id]);

  useEffect(() => {
    const finalPageId = activePageData?._id || activePageData?.page?._id;
    setParams({ workspace_id, group_id, page_id, page_type, active_page_id: finalPageId, activePageData });
  }, [workspace_id, group_id, page_id, page_type, activePageData]);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleWindowResize, { passive: true });
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    if (windowWidth < 1280) {
      setIsMenu(false);
    } else {
      setIsMenu(true);
    }
    return () => window.removeEventListener("resize", handleResize);
  }, [windowWidth, workspace_id]);

  const loadWorkspaces = useCallback(
    async (pre_fetch) => {
      if (!pre_fetch) setWorkspaceDetailsLoading(true);
      try {
        let url = "/portal/users/workspaces/list";
        const pageQuery = page_id ? `&page=${page_id}` : document_id ? `&document=${document_id}` : form_id ? `&form=${form_id}` : "";
        url = `/portal/users/workspaces/list?workspace=${workspace_id}&group=${group_id || ""}${pageQuery}`;
        const { data: resData } = await apiRequest("GET", url);

        if (resData.status === 200) {
          if ((!Array.isArray(resData.data) || resData.data.length === 0) && !workspace_paused) {
            navigate("/permission-denied");
          }

          const result = resData.data;

          if (pre_fetch) {
            return result;
          }

          setWorkspaces(result);
        }
      } catch (err) {
      } finally {
        setWorkspaceDetailsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace_id],
  );

  const isOuterPage = outerPages.includes(window.location.href);

  useEffect(() => {
    const layout_type = workspaceDetails?.layout_type || site?.layout_type;
    const layout_style = workspaceDetails?.layout_style || site?.layout_style;
    setMenuType(layout_type === "TOP_NAVIGATION" ? "horizontal" : "vertical");
    setMordernDesign((!payment_required && layout_style === "modern") || false);
  }, [site?.layout_type, workspaceDetails?.layout_type, workspaceDetails?.layout_style, site?.layout_style, payment_required]);

  useEffect(() => {
    let sidepanelMaxWidth = workspaceDetails?.sidepanel_max_width;
    let useCustomSidebar = workspaceDetails?.menu_custom_template_enabled || false;
    let customSidebarTemplate = workspaceDetails?.left_menu_custom_template_html || "";

    if (workspaceDetails?.use_global_settings) {
      sidepanelMaxWidth = site?.sidepanel_max_width;
      useCustomSidebar = site?.menu_custom_template_enabled;
      customSidebarTemplate = site?.left_menu_custom_template_html;
    }

    setUseCustomSidbar(useCustomSidebar);
    setCustomSidebarHtml(customSidebarTemplate);
    setSidepanelWidth(device === "mobile" ? "336px" : sidepanelMaxWidth);
  }, [workspaceDetails, site, device]);

  useEffect(() => {
    if (isOuterPage) {
      setWorkspaceDetails({});
    }
  }, [isOuterPage]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  useEffect(() => {
    if (workspaces.length) {
      const currentWorkspace = workspaces.find((workspace) => workspace._id === workspace_id || workspace.slug === workspace_id);
      if (!currentWorkspace && !workspace_paused) navigate("/permission-denied");
      setWorkspaceDetails(currentWorkspace || workspaces[0]);
      socket?.emit("join-workspace-request", { workspace_id: currentWorkspace?._id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces.length, workspace_id]);

  const loadMenus = useCallback(
    async (from, pre_fetch = false) => {
      const controller = new AbortController();
      const signal = controller.signal;
      if (!workspace_id || !workspaceDetails?.slug || ![workspaceDetails?._id, workspaceDetails?.slug].includes(workspace_id) || (!menu_required && !pre_fetch)) {
        setMenus([]);
        setSimulatedMenus([]);
        return;
      }

      try {
        if (from !== "menu" && !pre_fetch) {
          setMenus([]);
          setSimulatedMenus([]);
          setMenusLoaded(true);
        }

        let url = `/portal/menu/${workspace_id}`;
        const pageQuery = page_id ? `&page=${page_id}` : document_id ? `&document=${document_id}` : form_id ? `&form=${form_id}` : "";
        url = `/portal/menu/${workspace_id}?group=${group_id || ""}${pageQuery}`;

        const { data: menuData } = await apiRequest("GET", url, {}, { signal });

        if (menuData.status === 200) {
          const individualMenus = menuData.data.individualMenu.map((item) => ({
            ...(item.page || {}),
            headerMenu: false,
            type: item.page?.page_type !== "EXTERNAL_LINK" ? "internal" : "external",
            link: item.page?.page_type !== "EXTERNAL_LINK" ? `/workspace/${workspaceDetails?.slug || workspace_id}/embed?${item?.form_id ? "form" : item?.document_id ? "document" : "page"}=${item.page?.slug || item.page?._id}` : item.page?.page_link,
          }));

          const groupMenus = menuData.data.menu.map((item) => ({
            ...item,
            type: "group",
            isOpen: item.slug === group_id || item._id === group_id,
            submenu: item.permissions.map((permission) => ({
              ...permission.page,
              name: permission?.page?.name ?? permission?.page_alias,
              image: permission?.image || permission?.page?.image,
              groupId: item.slug || item._id,
              isOpen: !permission?.is_group ? item.slug === group_id || item._id === group_id : permission?.pages?.some((page_item) => page_item?.page_slug === page_id || form_id || document_id) && item.slug === group_id,
              type: permission?.pages?.length > 0 ? "group" : permission.page?.page_type !== "EXTERNAL_LINK" ? "internal" : "external",
              is_group: permission?.is_group,
              link: permission?.is_group
                ? null
                : permission.page?.page_type !== "EXTERNAL_LINK"
                  ? `/workspace/${workspaceDetails?.slug || workspace_id}/embed?group=${item.slug || item._id}&${permission?.form_id ? "form" : permission?.document_id ? "document" : "page"}=${permission.page?.slug || permission.page?._id}`
                  : permission.page?.page_link,
              submenu: permission?.pages?.map((page) => ({
                ...page,
                image: page?.page?.image || page?.image,
                name: page.page?.name || page.slug,
                slug: page.page?.slug,
                groupId: item.slug || item._id,
                link: page.page_type !== "EXTERNAL_LINK" ? `/workspace/${workspaceDetails?.slug || workspace_id}/embed?group=${item.slug || item._id}&${page?.form_id ? "form" : page?.document_id ? "document" : "page"}=${page.page_slug || page.page?._id}` : page.page_link,
                type: page.page_type !== "EXTERNAL_LINK" ? "internal" : "external",
              })),
            })),
          }));

          let finalGroupMenu = [];
          for (const groupMenu of groupMenus) {
            if (groupMenu?.flatten_pages) {
              const submenuWithImage = groupMenu.submenu.map((sub, index) => {
                return index === 0 && groupMenu.submenu.length === 1 ? { ...sub, image: groupMenu.image, sub: true } : { ...sub, sub: true };
              });
              finalGroupMenu.push(...submenuWithImage);
            } else {
              finalGroupMenu.push(groupMenu);
            }
          }

          let finalMenu = [...individualMenus, ...finalGroupMenu];

          if (!individualMenus.length && finalGroupMenu.length === 1 && !mordernDesign) {
            finalMenu = finalGroupMenu.flatMap((item) => item.submenu.map((item) => ({ ...item, sub: true })));
          }

          if (!individualMenus.length && finalMenu.length === 1 && finalMenu[0].submenu.length > 1) {
            finalMenu = finalMenu.flatMap((item) => item.submenu.map((item) => ({ ...item, sub: true })));
          }

          finalMenu = finalMenu.map((item) => {
            if (item.submenu && item.submenu.length === 1 && item.submenu[0].submenu && item.submenu[0].submenu.length > 1) {
              item.submenu = item.submenu.flatMap((sub) => sub.submenu || []);
            }
            return item;
          });

          if (pre_fetch) {
            return finalMenu;
          }

          setMenus(finalMenu);
          if (!keyword) {
            setSimulatedMenus(finalMenu);
          } else {
            setSimulatedMenus(searchMenu(finalMenu, keyword));
          }
          setMenusLoaded(true);

          return finalMenu;
        }
      } catch (err) {
      } finally {
      }
      return () => {
        controller.abort();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace_id, workspaceDetails?.slug, workspaceDetails?._id, menu_required],
  );

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  const containerClass = {
    default: "w-full flex flex-grow transition-all duration-200 bg-backgroundColor",
    vertical: classNames(
      "scrollbar w-full flex flex-grow transition-all duration-200 justify-center overflow-auto bg-backgroundColor",
      isMenu && !isOuterPage && workspaceDetails?.workspace_type !== "JWT_FULL_EMBED" && menus?.length > 1 && menuType === "vertical" ? " justify-center" : "max-w-full",
    ),
    horizontal: classNames(
      "scrollbar w-full flex flex-grow transition-all duration-200 justify-center overflow-auto bg-backgroundColor",
      isMenu && !isOuterPage && workspaceDetails?.workspace_type !== "JWT_FULL_EMBED" && menus?.length > 1 && menuType === "vertical" ? " justify-center h-full" : "max-w-full ",
    ),
  };

  useEffect(() => {
    if (windowWidth < 768) {
      if (isMenu) {
        document.querySelector("html").style.overflow = "hidden";
        document.querySelector("body").style.overflow = "hidden";
      } else {
        document.querySelector("html").style.overflow = "";
        document.querySelector("body").style.overflow = "";
      }
    }
  }, [isMenu, windowWidth]);

  const getActiveNotifications = useCallback(async () => {
    if (workspaceDetails?._id) {
      try {
        setActiveNotifications({});
        const { data } = await apiRequest("GET", "/portal/workspaces/:workspace_id/notifications/active", { params: { workspace_id: workspaceDetails?._id }, queries: { group: group_id, page: page_id, document: document_id, form: form_id } });
        setActiveNotifications(data?.data);
      } catch (error) {
        setActiveNotifications({});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceDetails?._id]);

  useEffect(() => {
    getActiveNotifications();
  }, [getActiveNotifications]);

  useEffect(() => {
    let observer;
    let rafId;

    const updateHeight = () => {
      if (elementRef.current) {
        const height = elementRef.current.getBoundingClientRect().height;
        setNoticeHeight(height);
      } else {
        setNoticeHeight(0);
      }
    };

    const waitForElement = () => {
      if (elementRef.current) {
        updateHeight();

        observer = new ResizeObserver(() => {
          updateHeight();
        });

        observer.observe(elementRef.current);
      } else {
        rafId = requestAnimationFrame(waitForElement);
      }
    };

    waitForElement();

    return () => {
      if (observer) observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [activeNotifications?.content, workspaceDetails?._id]);

  useEffect(() => {
    if (!activeNotifications?.content && !elementRef.current) {
      setNoticeHeight(0);
    }
  }, [activeNotifications?.content]);

  const groupUpdateResponseCondition = (data) => {
    const isEnabled = ["users", "teams", "role", "pages", "flatten_pages", "image", "slug", "delete"].includes(data.type);

    return isEnabled;
  };

  useSocketListeners([
    {
      event: "notification-response",
      handler: getActiveNotifications,
    },
    {
      event: "workspace-settings-updated-response",
      title: "Workspace settings",
      message: "Workspace settings has been updated",
      condition: (data) => allowed_types.includes(data?.type) && data?._id === workspaceDetails?._id,
      handler: (data) => setWorkspaceDetails({ ...workspaceDetails, ...data }),
    },
    {
      event: "workspace-user-update-response",
      preFetch: true,
      title: "Workspace permissions changed",
      message: "Your workspace access has changed, sync now",
      currentData: workspaces,
      handler: () => loadWorkspaces(true),
      onSync: (updated_workspaces) => {
        setWorkspaces(updated_workspaces);
      },
    },
    {
      event: "group-update-response",
      condition: groupUpdateResponseCondition,
      title: "Menu permissions changed",
      message: "Your menu access has changed, sync now",
      handler: () => loadMenus("socket", true),
      onSync: async (updated_menus) => {
        setMenus(updated_menus);
        setSimulatedMenus(updated_menus);
        if (no_content) {
          try {
            const { data: resData } = await apiRequest("GET", workspace_id ? `/portal/users/${workspace_id}/page` : `/portal/users/workspaces/page`, {}, { returnErrorWithstatus: true });
            const navigate_link = getNavigateLink(resData);
            navigate(navigate_link);
          } catch (error) {
            if (error?.response?.data?.status === 403 && ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(error?.response?.data?.code)) {
              navigate(`/workspace/${workspace_id}/workspace-paused`, {
                state: {
                  message: error?.response?.data?.message,
                  pause_expires_at: error?.response?.data?.pause_expires_at,
                },
              });
            } else {
              navigate("/permission-denied");
            }
          }
        }
      },
    },
    {
      event: "user-permission-update-response",
      condition: (data) => data?.workspace_slug === workspace_id && data?.changed_slugs?.length > 0,
      title: "Individual menus changed",
      message: "Your menu access has changed, sync now",
      handler: () => loadMenus("socket", true),
      onSync: async (updated_menus) => {
        setMenus(updated_menus);
        setSimulatedMenus(updated_menus);
        if (no_content) {
          try {
            const { data: resData } = await apiRequest("GET", workspace_id ? `/portal/users/${workspace_id}/page` : `/portal/users/workspaces/page`, {}, { returnErrorWithstatus: true });
            const navigate_link = getNavigateLink(resData);
            navigate(navigate_link);
          } catch (error) {
            if (error?.response?.data?.status === 403 && ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(error?.response?.data?.code)) {
              navigate(`/workspace/${workspace_id}/workspace-paused`, {
                state: {
                  message: error?.response?.data?.message,
                  pause_expires_at: error?.response?.data?.pause_expires_at,
                },
              });
            } else {
              navigate("/permission-denied");
            }
          }
        }
      },
    },
  ]);

  if (!site?._id) {
    return (
      <div className={classNames("flex  h-full flex-grow items-center justify-center pt-16")}>
        <Preloader />
      </div>
    );
  }

  return (
    <WorkspaceContext.Provider
      value={{
        loading: workspaceDetailsLoading,
        setLoading: setWorkspaceDetailsLoading,
        list: workspaces,
        setList: setWorkspaces,
        details: workspaceDetails,
        setDetails: setWorkspaceDetails,
        workspacePaymentModalOpen,
        setWorspacePaymentModalOpen,
        planDetails,
        setPlanDetails,
        isPlanDetailsLoaded,
        setIsPlanDetailsLoaded,
        menuType,
        params,
        setParams,
        menusLoaded,
      }}>
      {!nested ? (
        <>
          {activeNotifications?.content && (
            <NotificationBanner
              // ref={elementRef}
              data={activeNotifications}
              setActiveNotifications={setActiveNotifications}
              user={user}
            />
          )}
          {mordernDesign && menuType === "vertical" && menus?.length > 1 ? (
            <>
              <MordernDesign
                mordernDesign={mordernDesign}
                sidepanelWidth={sidepanelWidth}
                isMenu={isMenu}
                setIsMenu={setIsMenu}
                menus={menus}
                setMenus={setMenus}
                simulatedMenus={simulatedMenus}
                setSimulatedMenus={setSimulatedMenus}
                keyword={keyword}
                setKeyword={setKeyword}
                handleMenuClick={device === "mobile" ? () => setIsMenu(false) : () => {}}
                containerClass={containerClass}
                menuType={menuType}
                device={device}
                menusLoaded={menusLoaded}
                isNotification={activeNotifications?.content}
              />
            </>
          ) : (
            <div
              className="relative flex flex-shrink flex-grow flex-col"
              style={{ maxHeight: `calc(100vh - ${noticeHeight}px)` }}>
              <Header
                isMenu={isMenu}
                setIsMenu={setIsMenu}
                menusLoaded={menusLoaded}
                menuType={menuType}
                menus={menus}
                mordernDesign={mordernDesign}
              />
              {workspaceDetailsLoading && !outerPages.includes(window.location.href) ? (
                <div className={classNames("flex h-full flex-grow items-center justify-center")}>
                  <Preloader />
                </div>
              ) : (
                <div
                  className={classNames("relative flex flex-shrink flex-grow", menuType !== "vertical" ? "flex-col" : "")}
                  style={{
                    maxHeight: activeNotifications?.content ? `calc(${height - noticeHeight}px - ${headerHeight})` : `calc(100vh - ${headerHeight})`,
                  }}>
                  {outerPages.includes(window.location.href) ? (
                    <></>
                  ) : workspaceDetails?.workspace_type === "JWT_FULL_EMBED" ? (
                    <></>
                  ) : (
                    <>
                      {menus?.length > 1 && !payment_required && (
                        <NavigationRouter
                          sidepanelWidth={sidepanelWidth}
                          menuType={menuType}
                          isMenu={isMenu}
                          setIsMenu={setIsMenu}
                          menus={menus}
                          setMenus={setMenus}
                          simulatedMenus={simulatedMenus}
                          setSimulatedMenus={setSimulatedMenus}
                          menusLoaded={menusLoaded}
                          keyword={keyword}
                          setKeyword={setKeyword}
                          useCustomSidbar={useCustomSidbar}
                          customSidebarHtml={customSidebarHtml}
                          handleMenuClick={device === "mobile" ? () => setIsMenu(false) : () => {}}
                          activeNotifications={activeNotifications}
                        />
                      )}
                    </>
                  )}
                  <div
                    className={!payment_required ? containerClass[menuType] : "flex w-full"}
                    style={{
                      maxWidth: menus?.length > 1 && !payment_required ? (device === "mobile" || !isMenu ? "100%" : menuType === "vertical" ? `calc(100% - ${sidepanelWidth})` : "100%") : "100%",
                      marginLeft: menus?.length > 1 && !payment_required ? (device === "mobile" || !isMenu ? 0 : menuType === "vertical" ? `${sidepanelWidth}` : 0) : 0,
                    }}>
                    <Outlet />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="bg-backgroundColor">
          <Outlet />
        </div>
      )}
      {site?._id && user?._id && site?.user_inactivity_logout_enabled > 0 && (
        <LogoutTimer
          site={site}
          me={user}
          onLogout={logoutRedirect}
        />
      )}
    </WorkspaceContext.Provider>
  );
};

export default Dashboardlayout;
